(function () {
  const TOKYO_TIME_ZONE = 'Asia/Tokyo';
  const CLOSED_STATUS = 'closed';
  const NOTICE_STATUS = 'notice';
  const OPEN_STATUS = 'open';
  const INITIAL_EXCEPTION_LIMIT = 3;
  const COLLAPSED_EXCEPTION_CLASS = 'business-calendar__exception--collapsed';

  function datePartsInTokyo(date) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: TOKYO_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);

    return Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
  }

  function todayInTokyo(date = new Date()) {
    const parts = datePartsInTokyo(date);
    return `${parts.year}-${parts.month}-${parts.day}`;
  }

  function parseDate(dateString) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateString || ''));
    if (!match) return null;
    return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  }

  function formatDate(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function addDays(dateString, days) {
    const date = parseDate(dateString);
    if (!date) return '';
    date.setUTCDate(date.getUTCDate() + days);
    return formatDate(date);
  }

  function normalizeExceptions(rawExceptions) {
    const byDate = {};

    if (!Array.isArray(rawExceptions)) return byDate;

    rawExceptions.forEach((entry) => {
      const date = String(entry && entry.date ? entry.date : '').trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;

      const rawStatus = String(entry.status || '').trim().toLowerCase();
      const status = rawStatus === NOTICE_STATUS ? NOTICE_STATUS : CLOSED_STATUS;
      byDate[date] = {
        date,
        status,
        title: String(entry.title || '').trim(),
        message: String(entry.message || '').trim(),
        resumeDate: String(entry.resume_date || entry.resumeDate || '').trim(),
      };
    });

    return byDate;
  }

  function findNextBusinessDate(dateString, exceptionsByDate) {
    for (let offset = 1; offset <= 366; offset += 1) {
      const nextDate = addDays(dateString, offset);
      if (!nextDate) return '';
      if (!exceptionsByDate[nextDate] || exceptionsByDate[nextDate].status !== CLOSED_STATUS) return nextDate;
    }

    return '';
  }

  function resolveResumeDate(exception, exceptionsByDate) {
    if (!exception) return '';

    if (
      exception.resumeDate &&
      exception.resumeDate > exception.date &&
      (!exceptionsByDate[exception.resumeDate] || exceptionsByDate[exception.resumeDate].status !== CLOSED_STATUS)
    ) {
      return exception.resumeDate;
    }

    return findNextBusinessDate(exception.date, exceptionsByDate);
  }

  function getBusinessStatus(dateString, exceptionsByDate) {
    const exception = exceptionsByDate[dateString];

    if (exception && exception.status === CLOSED_STATUS) {
      return {
        state: CLOSED_STATUS,
        exception,
        nextBusinessDate: resolveResumeDate(exception, exceptionsByDate),
      };
    }

    if (exception && exception.status === NOTICE_STATUS) {
      return {
        state: NOTICE_STATUS,
        exception,
        nextBusinessDate: dateString,
      };
    }

    return {
      state: OPEN_STATUS,
      exception: null,
      nextBusinessDate: dateString,
    };
  }

  function readExceptions(root) {
    const source = root.querySelector('[data-business-calendar-exceptions]');
    if (!source) return {};

    try {
      return normalizeExceptions(JSON.parse(source.textContent));
    } catch (_error) {
      return {};
    }
  }

  function rootLabel(root, key, fallback) {
    return root.dataset[key] || fallback;
  }

  function customerText(text) {
    return String(text || '').replace(/休業/g, '店休日');
  }

  function statusText(root, status) {
    if (status.state === CLOSED_STATUS) return rootLabel(root, 'businessCalendarClosedLabel', '本日オンラインショップ店休日');
    if (status.state === NOTICE_STATUS) return rootLabel(root, 'businessCalendarNoticeLabel', 'お知らせあり');
    return rootLabel(root, 'businessCalendarOpenLabel', '本日営業');
  }

  function shortDateLabel(dateString) {
    const date = parseDate(dateString);
    if (!date) return '';

    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    return `${date.getUTCMonth() + 1}月${date.getUTCDate()}日（${weekdays[date.getUTCDay()]}）`;
  }

  function fullDateLabel(dateString) {
    const date = parseDate(dateString);
    if (!date) return '';

    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    return `${date.getUTCFullYear()}年${date.getUTCMonth() + 1}月${date.getUTCDate()}日（${weekdays[date.getUTCDay()]}）`;
  }

  function statusHeadline(root, status, dateString) {
    const dateLabel = shortDateLabel(dateString);
    if (!dateLabel) return statusText(root, status);

    if (status.state === CLOSED_STATUS) {
      const closedDayLabel = rootLabel(root, 'businessCalendarClosedDayLabel', 'オンラインショップ店休日');
      return `本日 ${dateLabel}は${closedDayLabel}です`;
    }
    if (status.state === NOTICE_STATUS) return `本日 ${dateLabel}はお知らせがあります`;
    return `本日 ${dateLabel}は営業日です`;
  }

  function statusDetail(root, status) {
    if (status.state === CLOSED_STATUS) {
      const base = rootLabel(root, 'businessCalendarClosedDetail', '発送・お問い合わせ返信は翌営業日以降に対応します。');
      const note = status.exception && customerText(status.exception.message);
      const detail = note || base;
      if (!status.nextBusinessDate) return detail;
      return `${detail} ${rootLabel(root, 'businessCalendarNextBusinessDay', '次回営業日')}：${
        shortDateLabel(status.nextBusinessDate) || status.nextBusinessDate
      }`;
    }

    if (status.exception && status.exception.message) return customerText(status.exception.message);
    if (status.exception && status.exception.title) return customerText(status.exception.title);

    return rootLabel(root, 'businessCalendarOpenDetail', 'ご注文は24時間受付。発送・お問い合わせ返信は通常2営業日以内です。');
  }

  function updateStatus(root, exceptionsByDate, today) {
    const text = root.querySelector('[data-business-calendar-status-text]');
    const detail = root.querySelector('[data-business-calendar-status-detail]');
    if (!text && !detail) return;

    const status = getBusinessStatus(today, exceptionsByDate);
    root.dataset.businessCalendarState = status.state;

    if (text) text.textContent = statusHeadline(root, status, today);
    if (detail) detail.textContent = statusDetail(root, status);
  }

  function monthLabel(date) {
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
    }).format(date);
  }

  function dayLabel(date) {
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    }).format(date);
  }

  function monthStart(dateString) {
    const date = parseDate(dateString);
    if (!date) return parseDate(todayInTokyo());
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  }

  function shiftMonth(date, offset) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + offset, 1));
  }

  function createCell(root, dayDate, currentMonth, today, exceptionsByDate) {
    const dateString = formatDate(dayDate);
    const status = getBusinessStatus(dateString, exceptionsByDate);
    const day = document.createElement('td');
    const inner = document.createElement('div');
    const dateText = document.createElement('span');
    const stateText = document.createElement('span');
    const isCurrentMonth = dayDate.getUTCMonth() === currentMonth.getUTCMonth();
    const stateLabel = statusText(root, status);
    const closedCalendarLabel = rootLabel(root, 'businessCalendarClosedCalendarLabel', '店休日');

    inner.className = 'business-calendar__day';
    if (!isCurrentMonth) inner.classList.add('business-calendar__day--muted');
    if (dateString === today) inner.classList.add('business-calendar__day--today');
    if (status.state === CLOSED_STATUS) inner.classList.add('business-calendar__day--closed');
    if (status.state === NOTICE_STATUS) inner.classList.add('business-calendar__day--notice');

    dateText.className = 'business-calendar__date';
    dateText.textContent = String(dayDate.getUTCDate());

    stateText.className = 'business-calendar__state';
    stateText.textContent =
      status.state === OPEN_STATUS
        ? rootLabel(root, 'businessCalendarOpenDayLabel', '営業')
        : status.state === CLOSED_STATUS
          ? closedCalendarLabel
          : stateLabel;

    inner.setAttribute('aria-label', `${dayLabel(dayDate)} ${stateText.textContent}`);
    inner.append(dateText, stateText);

    if (status.exception && status.exception.title) {
      const noteText = document.createElement('span');
      noteText.className = 'business-calendar__state';
      noteText.textContent = customerText(status.exception.title);
      inner.append(noteText);
    }

    day.append(inner);
    return day;
  }

  function visibleExceptions(exceptionsByDate, today) {
    return Object.values(exceptionsByDate)
      .filter((exception) => exception.date >= today)
      .sort((first, second) => first.date.localeCompare(second.date));
  }

  function exceptionLabel(root, exception) {
    if (exception.status === CLOSED_STATUS) return rootLabel(root, 'businessCalendarExceptionClosedLabel', 'オンラインショップ店休日');
    return rootLabel(root, 'businessCalendarExceptionNoticeLabel', 'お知らせ');
  }

  function resumeDateLabel(root, dateString) {
    const template = rootLabel(root, 'businessCalendarResumeDateLabel', '対応再開予定：__DATE__');
    return template.replace('__DATE__', fullDateLabel(dateString) || dateString);
  }

  function createExceptionItem(root, exception, exceptionsByDate) {
    const item = document.createElement('li');
    const marker = document.createElement('span');
    const body = document.createElement('span');
    const title = document.createElement('strong');
    const kind = document.createElement('span');

    item.className = 'business-calendar__exception';
    body.className = 'business-calendar__exception-body';
    marker.className =
      exception.status === CLOSED_STATUS
        ? 'business-calendar__marker business-calendar__marker--closed'
        : 'business-calendar__marker business-calendar__marker--notice';
    marker.setAttribute('aria-hidden', 'true');

    title.className = 'business-calendar__exception-date';
    title.textContent = fullDateLabel(exception.date) || exception.date;
    kind.className = 'business-calendar__exception-kind';
    kind.textContent = exceptionLabel(root, exception);
    body.append(title, kind);

    const defaultClosedMessage = rootLabel(root, 'businessCalendarClosedDetail', '発送・お問い合わせ返信は翌営業日以降に対応します。');
    const message = exception.message || (exception.status === CLOSED_STATUS ? defaultClosedMessage : '');

    [
      { className: 'business-calendar__exception-note', text: exception.title },
      { className: 'business-calendar__exception-message', text: message },
    ].forEach((lineData) => {
      if (!lineData.text) return;
      const line = document.createElement('span');
      line.className = lineData.className;
      line.textContent = customerText(lineData.text);
      body.append(line);
    });

    if (exception.status === CLOSED_STATUS) {
      const nextBusinessDate = resolveResumeDate(exception, exceptionsByDate);
      if (nextBusinessDate) {
        const line = document.createElement('span');
        line.className = 'business-calendar__exception-resume';
        line.textContent = resumeDateLabel(root, nextBusinessDate);
        body.append(line);
      }
    }

    item.append(marker, body);
    return item;
  }

  function hideExceptionDisclosure(root, list) {
    const button = root.querySelector('[data-business-calendar-exception-toggle]');
    if (!button) return;

    button.hidden = true;
    button.setAttribute('aria-expanded', 'false');
    if (list) {
      list.querySelectorAll('.business-calendar__exception').forEach((item) => {
        item.hidden = false;
        item.classList.remove(COLLAPSED_EXCEPTION_CLASS);
      });
    }
  }

  function scrollToRevealedException(root, item) {
    if (!item || typeof item.scrollIntoView !== 'function') return;

    const view = root.ownerDocument && root.ownerDocument.defaultView;
    const scroll = () => {
      item.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    };

    if (view && typeof view.requestAnimationFrame === 'function') {
      view.requestAnimationFrame(scroll);
    } else {
      scroll();
    }
  }

  function updateExceptionDisclosure(root, list, options = {}) {
    const button = root.querySelector('[data-business-calendar-exception-toggle]');
    if (!button) return;

    const items = Array.from(list.querySelectorAll('.business-calendar__exception'));
    if (items.length <= INITIAL_EXCEPTION_LIMIT) {
      hideExceptionDisclosure(root, list);
      return;
    }

    const expanded = button.getAttribute('aria-expanded') === 'true';
    let firstRevealedItem = null;

    items.forEach((item, index) => {
      const shouldCollapse = !expanded && index >= INITIAL_EXCEPTION_LIMIT;
      const wasCollapsed = item.classList.contains(COLLAPSED_EXCEPTION_CLASS);

      item.hidden = shouldCollapse;
      item.classList.toggle(COLLAPSED_EXCEPTION_CLASS, shouldCollapse);

      if (expanded && wasCollapsed && index >= INITIAL_EXCEPTION_LIMIT && !firstRevealedItem) {
        firstRevealedItem = item;
      }
    });

    button.hidden = false;
    button.textContent = expanded
      ? rootLabel(root, 'businessCalendarExceptionsShowLessLabel', '閉じる')
      : rootLabel(root, 'businessCalendarExceptionsShowMoreLabel', 'もっと見る');

    if (expanded && options.scrollToRevealed) {
      scrollToRevealedException(root, firstRevealedItem);
    }
  }

  function bindExceptionDisclosure(root, list) {
    const button = root.querySelector('[data-business-calendar-exception-toggle]');
    if (!button || button.dataset.businessCalendarToggleBound === 'true') return;

    button.dataset.businessCalendarToggleBound = 'true';
    button.addEventListener('click', () => {
      const expanded = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      updateExceptionDisclosure(root, list, { scrollToRevealed: !expanded });
    });
  }

  function renderExceptions(root, exceptionsByDate, today) {
    const list = root.querySelector('[data-business-calendar-exception-list]');
    if (!list) return;

    const emptyFallback = root.querySelector('[data-business-calendar-exception-empty]');
    const exceptions = visibleExceptions(exceptionsByDate, today);
    if (emptyFallback) emptyFallback.hidden = true;
    list.replaceChildren();

    if (exceptions.length === 0) {
      hideExceptionDisclosure(root, list);

      if (emptyFallback) {
        emptyFallback.hidden = false;
        return;
      }

      const empty = document.createElement('li');
      empty.className = 'business-calendar__fallback';
      empty.textContent = rootLabel(root, 'businessCalendarExceptionsEmptyLabel', '現在、登録されているオンラインショップ店休日・お知らせはありません。');
      list.append(empty);
      return;
    }

    exceptions.forEach((exception) => {
      list.append(createExceptionItem(root, exception, exceptionsByDate));
    });

    bindExceptionDisclosure(root, list);
    updateExceptionDisclosure(root, list);
  }

  function renderCalendar(root, exceptionsByDate, today) {
    const target = root.querySelector('[data-business-calendar-grid]');
    const heading = root.querySelector('[data-business-calendar-month-heading]');
    if (!target || !heading) return;

    const currentMonth = root._businessCalendarMonth || monthStart(today);
    const firstDay = new Date(currentMonth);
    const start = new Date(firstDay);
    start.setUTCDate(1 - firstDay.getUTCDay());

    const table = document.createElement('table');
    const thead = document.createElement('thead');
    const tbody = document.createElement('tbody');
    const headerRow = document.createElement('tr');
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];

    table.className = 'business-calendar__table';
    table.setAttribute('aria-label', rootLabel(root, 'businessCalendarCalendarLabel', '営業日カレンダー'));

    weekdays.forEach((weekday) => {
      const th = document.createElement('th');
      th.scope = 'col';
      th.textContent = weekday;
      headerRow.append(th);
    });

    thead.append(headerRow);

    for (let week = 0; week < 6; week += 1) {
      const row = document.createElement('tr');
      for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
        const dayDate = new Date(start);
        dayDate.setUTCDate(start.getUTCDate() + week * 7 + dayIndex);
        row.append(createCell(root, dayDate, currentMonth, today, exceptionsByDate));
      }
      tbody.append(row);
    }

    table.append(thead, tbody);
    target.replaceChildren(table);
    heading.textContent = monthLabel(currentMonth);
  }

  function initRoot(root) {
    const exceptionsByDate = readExceptions(root);
    const today = todayInTokyo();
    root._businessCalendarMonth = monthStart(today);

    updateStatus(root, exceptionsByDate, today);
    renderCalendar(root, exceptionsByDate, today);
    renderExceptions(root, exceptionsByDate, today);

    const previous = root.querySelector('[data-business-calendar-prev]');
    const next = root.querySelector('[data-business-calendar-next]');

    if (previous) {
      previous.addEventListener('click', () => {
        root._businessCalendarMonth = shiftMonth(root._businessCalendarMonth, -1);
        renderCalendar(root, exceptionsByDate, today);
      });
    }

    if (next) {
      next.addEventListener('click', () => {
        root._businessCalendarMonth = shiftMonth(root._businessCalendarMonth, 1);
        renderCalendar(root, exceptionsByDate, today);
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-business-calendar-root]').forEach(initRoot);
  }

  const api = {
    addDays,
    findNextBusinessDate,
    fullDateLabel,
    getBusinessStatus,
    normalizeExceptions,
    resolveResumeDate,
    todayInTokyo,
    visibleExceptions,
  };

  if (typeof globalThis !== 'undefined') globalThis.BusinessCalendar = api;

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  }
})();
